import { useEffect, useState } from "react";
import { CloudUpload, Link2Off } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DriveSetupGuide } from "./DriveSetupGuide";

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

/** Off-site copies: monthly keeper → a folder in the owner's Google Drive. Only sealed ciphertext ever leaves the app. */
export function DriveOffsiteCard({ lastRun }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/drive/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const flag = new URLSearchParams(window.location.search).get("drive");
    if (flag === "connected") toast.success("Google Drive connected. The monthly archive will be copied there.");
    else if (flag) toast.error(`Google Drive connection did not finish (${flag}). Try again.`);
  }, []);
  if (!st) return null;
  const connect = async () => {
    try { window.location.href = (await api.get("/admin/drive/connect")).data.authorization_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/admin/drive/disconnect"); toast.success("Drive disconnected. Nothing in Drive was deleted."); load(); };
  const sendNow = async () => {
    if (!lastRun) return;
    setBusy(true);
    try { const r = await api.post(`/admin/drive/send/${lastRun.id}`); toast.success(`Copied ${r.data.copy.name} to Drive — it's in “Root & Rose archives”.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-stone-200 p-4 space-y-2" data-testid="drive-offsite-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Off-site copy · Google Drive</div>
          <p className="text-xs text-stone-600 mt-1" data-testid="drive-status">
            {!st.configured && "Not set up yet — add the Google client ID and secret, then connect the owner's Drive."}
            {st.configured && !st.connected && "Ready to connect. Each monthly archive will be copied to a folder named “Root & Rose archives”; the newest 12 are kept."}
            {st.connected && <>Connected{st.account ? ` as ${st.account}` : ""} · folder “{st.folder}” · {st.last_copy ? <>last copy {when(st.last_copy.at)} · {st.last_copy.link ? <a href={st.last_copy.link} target="_blank" rel="noreferrer" className="underline" data-testid="drive-last-copy-link">{st.last_copy.name}</a> : st.last_copy.name}</> : "no copy sent yet — send a test copy to finish setup"}</>}
          </p>
          {st.last_error && <p className="text-xs text-rose-800 mt-1" data-testid="drive-error">Last attempt failed ({st.last_error.error}) at {when(st.last_error.at)}.</p>}
          <p className="text-[11px] text-stone-500 mt-1">The archive stays sealed with this app's key, so Drive only holds ciphertext. The app can see only the folder it created.</p>
        </div>
        <div className="flex gap-2">
          {st.configured && !st.connected && <button onClick={connect} data-testid="drive-connect-btn" className="amber-btn text-xs py-1.5 inline-flex items-center gap-1"><CloudUpload className="w-3.5 h-3.5" /> Connect Google Drive</button>}
          {st.connected && <button disabled={busy || !lastRun} onClick={sendNow} data-testid="drive-send-btn" className="amber-btn text-xs py-1.5 inline-flex items-center gap-1"><CloudUpload className="w-3.5 h-3.5" /> {busy ? "Sending…" : st.last_copy ? "Send latest archive now" : "Send a test copy now"}</button>}
          {st.connected && <button onClick={disconnect} data-testid="drive-disconnect-btn" className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 text-stone-700"><Link2Off className="w-3.5 h-3.5" /> Disconnect</button>}
        </div>
      </div>
      <DriveSetupGuide st={st} />
    </div>
  );
}
